import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconPlus, IconPhoneCall } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@core-ui/Components/ui/button.tsx';
import NumbersTable from '@features/numbers/components/Numbers/NumbersTable.tsx';
import  { LogLevels, type NumberPool, type NumbersForm, type UpdateNumberV3Payload } from '@types';
import { SearchInput } from '@ui/shared/searchInput.tsx';
import { PaginationControls } from '@ui/shared/paginationControls.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@core-ui/Components/ui/card.tsx';
import { usePaginationData } from '@hooks/usePaginationData.ts';
import useCallerIdNumbersEdgeApi from '@features/numbers/hooks/useCallerIdNumbers.Api.ts';
import { getCurrentUser } from '@features/auth';
import ConfirmDeleteNumberDialog from '@features/numbers/components/Numbers/ConfirmDeleteDialog.tsx';
import CallerIdDialog from '@features/numbers/components/Numbers/CallerIdDialog.tsx';
import { CallerIdNewNumber } from '@features/numbers/components/Numbers/CallerIdNewNumber.tsx';
import { writeSuccessLog } from '@lib/logger/logSuccess.tsx';
import { paintToast } from '@lib/logger/paintToast.ts';
import { writeErrorLog } from '@lib/logger/logError.ts';

export function CallerId (): React.JSX.Element {
  const { t } = useTranslation();

  const { getNumbersAsync, deleteNumbersAsync, updateCallerIdAsync } = useCallerIdNumbersEdgeApi();

  type FormErrors = Partial<Record<
    'number' | 'location' | 'ownerName' | 'type' | 'active',
    string
  >>;

  const [serverNumbers, setServerNumbers] = useState<NumberPool[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openNewNumber, setOpenNewNumber] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 400);
  const [form, setForm] = useState<NumbersForm>({
    number: '',
    location: '',
    type: '',
    active: false,
    ownerId: null,
    ownerName: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const latestSearchRef = useRef<string>('');

  const user = getCurrentUser();

  useEffect(() => {
    const messageAlert = {
      formErrors,
      form,
      debouncedSearch,
      isEdit,
      editId,
      openDialog,
      confirmDeleteId
    };

    console.log('message_alert', messageAlert);
  }, []);

  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } = usePaginationData<NumberPool>({
    mode: 'server',
    totalItems: totalCount,
    onServerRequest: useCallback((page: number, limit: number) => {
      void fetchNumbers(page, limit, latestSearchRef.current);
    }, []),
    initialRowsPerPage: 10,
    initialPage: 1,
  });

  function useDebounce<T> (value: T, delay = 400): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const id = setTimeout(() => {
        setDebounced(value);
      }, delay);
      return () => {
        clearTimeout(id);
      };
    }, [value, delay]);
    return debounced;
  }

  function openEdit (number: NumberPool): void {
    setForm({
      id: number.id,
      number: number.number ?? '',
      location: number.location ?? '',
      type: number.type,
      ownerName: number.ownerName ?? null,
      ownerId: number.ownerId ?? null,
      active: number.active ?? false,
    });
    setFormErrors({});
    setIsEdit(true);
    setEditId(number.id);
    setOpenDialog(true);
  }

  function openNewNumberDialog (): void {
    setOpenNewNumber(true);
  }

  async function handleSave (): Promise<void> {
    try {
      setLoading(true);
      const payload: UpdateNumberV3Payload = {
        id: form.id,
        number: (form.number ?? '').trim(),
        location: (form.location ?? '').trim(),
        type: (form.type ?? ''),
        active: (form.active ?? null),
        ownerId: form.ownerId ?? null,
      };

      const res = await updateCallerIdAsync(payload);
      if (!res.success) {
        throw new Error(res.error?.message ?? 'Update failed');
      }
      await writeSuccessLog(
        t('adminConsole.numberDialog.updated', { lng: 'en' }),
        'NumbersTab.handleSave.update',
        { idNumberEdited: editId, numberEdited: form.number, locationEdited: form.location, typeEdited: form.type, activeEdited: form.active }
      );
      paintToast(LogLevels.SUCCESS, t('adminConsole.numberDialog.updated'));

      setForm((prev) => ({ ...prev }));
      setFormErrors({});
      setOpenDialog(false);
      await fetchNumbers(currentPage, rowsPerPage, latestSearchRef.current);
    } catch (e) {
      await writeErrorLog(
        t('adminConsole.numberDialog.saveError', { lng: 'en' }),
        'NumbersTab.handleSave',
        { editId }
      );
      paintToast(LogLevels.ERROR, t('adminConsole.numberDialog.saveError'));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate (): Promise<void> {
    try {
      setLoading(true);
      await fetchNumbers(currentPage, rowsPerPage, latestSearchRef.current);
    } catch (e) {
      await writeErrorLog(
        t('adminConsole.numberDialog.saveError', { lng: 'en' }),
        'NumbersTab.handleSave',
        { editId }
      );
      paintToast(LogLevels.ERROR, t('adminConsole.numberDialog.saveError'));
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete (id: string | null): Promise<void> {
    if (id == null) {
      return;
    }
    try {
      setLoading(true);
      const res = await deleteNumbersAsync(id);
      if (!res.success) {
        throw new Error(res.error?.message);
      }
      await writeSuccessLog(
        t('adminConsole.numberDeleteDialog.success', { lng: 'en' }),
        'NumbersTab.handleDelete',
        { idUserDeleted: id }
      );
      paintToast(LogLevels.SUCCESS, t('adminConsole.numberDeleteDialog.success'));
      await fetchNumbers(currentPage, rowsPerPage, latestSearchRef.current);
    } catch (err) {
      await writeErrorLog(
        t('adminConsole.numberDeleteDialog.error', { lng: 'en' }),
        'NumbersTab.handleDelete',
        { idUserDeleted: id, error: String((err as Error)?.message ?? err) }
      );
      paintToast(LogLevels.ERROR, t('adminConsole.numberDeleteDialog.error'));
    } finally {
      setLoading(false);
    }
  }

  async function fetchNumbers (page: number, limit: number, query: string): Promise<void> {
    if (query.includes('+')) {
      query = query.replaceAll('+', '');
    }

    setLoading(true);
    try {
      const res = await getNumbersAsync(page, limit, query, []);
      if (res.data == null) {
        throw new Error(res.error?.message ?? 'Error fetching numbers');
      }

      const currentUser = getCurrentUser();

      const normalized: NumberPool[] = res.data.map((number: NumberPool) => {
        const id  = String(number.id).trim();

        return {
          id,
          number: number.number,
          location: number.location,
          type: number.type,
          ownerName: currentUser?.userName ?? '',
          active: number.active,
          ownerId: number.ownerId ?? null,
          timesUsed: number.timesUsed,
          deletedAt: number.deletedAt ?? null,
        } satisfies NumberPool;
      });

      setServerNumbers(normalized);
      setTotalCount(res.totalCount ?? 0);
    } catch (err) {
      console.error(err);
      setServerNumbers([]);
      setTotalCount(0);
      await writeErrorLog(
        t('adminConsole.numbersTable.loadError', { lng: 'en' }),
        'NumbersTab.fetchNumbers',
        { page, limit, query, error: String((err as Error)?.message ?? err) }
      );
      paintToast(LogLevels.ERROR, t('adminConsole.numbersTable.loadError'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="p-6 rounded-2xl shadow-lg border border-border bg-card text-foreground">
      <CardHeader className="flex flex-col px-0 pt-0 pb-0">
        <div className="flex flex-row items-center">
          <IconPhoneCall className="font-bold h-5 w-5" />
          <CardTitle data-qa-id="callerId-title" className="text-2xl font-bold text-foreground ml-1">{t('settings.callerId.pageTitle')}</CardTitle>
        </div>
        <CardDescription className="px-0 m-0">
          {t('settings.callerId.description')}
        </CardDescription>
      </CardHeader>

      <CardContent className="px-0">
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
          <SearchInput
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            onClear={() => {
              setSearch('');
              setCurrentPage(1);
              void fetchNumbers(1, rowsPerPage, '');
            }}
            placeholder={t('adminConsole.numbersTable.searchNumber')}
            section="callerId"
          />
          <Button
            data-qa-id="add-callerId-button"
            onClick={openNewNumberDialog}
            className="bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
          >
            <IconPlus className="h-4 w-4" />
            {t('settings.callerId.addNumber')}
          </Button>
        </div>

        <NumbersTable
          loading={loading}
          numbers={serverNumbers}
          onEdit={openEdit}
          onAskDelete={(id) => {
            setConfirmDeleteId(id ?? null);
          }}
          skeletonRows={rowsPerPage}
          isSuperAdmin={user?.isSuperAdmin ?? false}
          isCallerId={true}
        />

        <PaginationControls
          mode="server"
          totalItems={totalCount}
          currentPage={currentPage}
          onPageChange={(p) => {
            setCurrentPage(p);
            void fetchNumbers(p, rowsPerPage, latestSearchRef.current);
          }}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={(n) => {
            setRowsPerPage(n);
            setCurrentPage(1);
            void fetchNumbers(1, n, latestSearchRef.current);
          }}
          isLoading={loading}
          className="mt-4"
        />
      </CardContent>

      <ConfirmDeleteNumberDialog
        open={confirmDeleteId !== null}
        number={serverNumbers.find(u => u.id === confirmDeleteId)?.number ?? undefined}
        onCancel={() => {
          setConfirmDeleteId(null);
        }}
        onConfirm={() => {
          if ((confirmDeleteId?.trim()) != null) {
            void handleDelete(confirmDeleteId);
          }
          setConfirmDeleteId(null);
        }}
      />

      <CallerIdDialog
        open={openDialog}
        setOpen={(v) => {
          setOpenDialog(v);
          if (!v) {
            setFormErrors({});
          }
        }}
        form={form}
        setForm={(f) => {
          setForm(f);
        }}
        onSave={() => {
          void handleSave();
        }}
        errors={formErrors}
      />

      <CallerIdNewNumber
        open={openNewNumber}
        onSave={() => {
          void handleCreate();
        }}
        setOpen={(v) => {
          setOpenNewNumber(v);
          if (!v) {
            setFormErrors({});
          }
        }}
        errors={formErrors}
      />

    </Card>
  );
}